import { cn } from "@/lib/utils";

/**
 * Branded "RD" loading spinner. The capital R and D letterform paths are
 * lifted verbatim from the ResetData wordmark (assets/resetdata-logo.svg —
 * paths 9 and 10), so the spinner is exactly on-brand. Animation: each
 * letter traces its outline on (stroke draw via pathLength-normalised
 * dashoffset), fills with coral #e73452, holds, fades, and loops. The D is
 * staggered behind the R inside the keyframes themselves (not via
 * animation-delay) so the loop restart stays in sync with the group fade.
 *
 * `size` is the rendered height in px; the mark is ~2:1 wide. Honours
 * prefers-reduced-motion by rendering the static filled mark.
 *
 * The `.rdsp-*` rules live in src/index.css, NOT here. They were a runtime
 * `<style>` element inside this SVG until CHP-1337; `style-src-elem` now
 * requires a nonce on those, and since this CSS is static the injector was
 * removed rather than nonce'd. Adding a `<style>` back here would need a nonce
 * threaded in from get-nonce, and would reintroduce an injector for no gain.
 */

// Capital R from the wordmark (occupies x 0–110.75 in the source viewBox).
const R_PATH =
  "M90.45,66.52c13.53-6.28,20.3-16.5,20.3-30.63,0-11.12-4.05-19.88-12.14-26.29C90.51,3.2,78.55,0,62.72,0H0v12.15h62.72c11.24,0,19.55,2.08,24.92,6.25,5.38,4.17,8.07,10,8.07,17.49,0,6.89-2.75,12.57-8.25,17.03-5.5,4.47-13.75,6.71-24.74,6.71H0v56.92h14.68v-44.77h63.26c4.83,0,8.34.87,10.51,2.63,2.18,1.75,3.26,4.87,3.26,9.34v32.81h14.5v-32.81c0-5.44-1.33-9.46-3.99-12.06-2.66-2.6-6.59-4.32-11.78-5.17";

// Capital D from the wordmark (occupies x 501.8–627.96 in the source
// viewBox); translated left below to sit beside the R. Re-anchored and
// reversed from the source path so the stroke trace starts at the inner
// bottom-left corner of the bowl (the hard right angle at 516.47,104.41)
// and sweeps rightward into the main body curve.
const D_PATH =
  "M516.47,104.41H564.69C580.4,104.3,592.36,99.79,600.58,90.91C608.8,82.03,612.91,71.18,612.91,58.37C612.91,44.36,608.9,33.15,600.86,24.75C592.82,16.35,580.77,12.15,564.7,12.15H501.8V0H564.7C584.27,0,599.71,5.32,611.01,15.95C622.3,26.59,627.95,40.84,627.95,58.37C627.95,75.9,622.3,89.98,611,100.61C599.7,111.24,584.27,116.56,564.69,116.56H501.79V59.64H516.47Z";

export function BrandedSpinner({
  size = 48,
  className,
  label = "Loading",
}: {
  size?: number;
  className?: string;
  label?: string;
}) {
  return (
    <svg
      role="status"
      aria-label={label}
      className={cn("select-none", className)}
      height={size}
      width={Math.round(size * 1.58)}
      viewBox="-8 -8 269 170"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g className="rdsp-mark">
        <path className="rdsp-r" d={R_PATH} pathLength={1} />
        <g transform="translate(-375 0)">
          <path className="rdsp-d" d={D_PATH} pathLength={1} />
        </g>
        <text className="rdsp-text" x="130" y="152" textAnchor="middle">
          LOADING
        </text>
      </g>
    </svg>
  );
}

/**
 * Full-page / route-level loading state. Use as the Suspense fallback for
 * lazy routes and for whole-surface loading (ProtectedRoute, dashboards).
 * Centers a large spinner in the available space; `min-h-[60vh]` keeps it
 * vertically centred even when the parent collapses to content height.
 */
export function PageLoader({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex min-h-[60vh] flex-1 items-center justify-center", className)}>
      <BrandedSpinner size={120} label={label} />
    </div>
  );
}

/**
 * Loading overlay for modals, dialogs, and panels — washes out the content
 * underneath while a mutation or refetch is in flight. The nearest
 * positioned ancestor defines its bounds, so give the parent `relative`
 * (DialogContent is already positioned). z-50 matches the dialog layer.
 */
export function LoadingOverlay({ label, className }: { label?: string; className?: string }) {
  return (
    <div
      className={cn(
        "absolute inset-0 z-50 flex items-center justify-center rounded-[inherit] bg-background/70 backdrop-blur-[2px]",
        className,
      )}
    >
      <BrandedSpinner size={72} label={label} />
    </div>
  );
}
